import React, { useEffect, useState } from "react";
import { api } from "../api/api";

const BROKERS = [
  { value: "angelone", label: "Angel One" },
  { value: "zerodha", label: "Zerodha (coming soon)", disabled: true },
  { value: "upstox", label: "Upstox (coming soon)", disabled: true },
];

export default function BrokerConnect() {
  const [status, setStatus] = useState({ connected: false, has_saved_config: false });
  const [broker, setBroker] = useState("angelone");
  const [form, setForm] = useState({ api_key: "", client_code: "", password_or_pin: "", totp_secret: "" });
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);       // full Connect Broker form
  const [quickOpen, setQuickOpen] = useState(false); // PIN-only quick connect form

  const refresh = () => api.brokerConfig().then(setStatus).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const connect = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.brokerConnect({ broker, ...form });
      setOpen(false);
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const quickConnect = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.brokerQuickConnect(pin);
      setQuickOpen(false);
      setPin("");
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    await api.brokerDisconnect();
    refresh();
  };

  const hasSaved = status.has_saved_config;

  return (
    <div style={styles.wrap}>
      <div style={styles.row}>
        <span style={{ ...styles.dot, background: status.connected ? "var(--accent)" : "var(--text-dim)" }} />
        <span style={styles.label}>
          {status.connected
            ? `${status.broker} · ${status.client_code} · Connected`
            : hasSaved
              ? `${status.broker} · ${status.client_code} · Not connected`
              : "Broker not connected"}
        </span>
        {status.connected ? (
          <button style={styles.smallBtnDanger} onClick={disconnect}>Disconnect</button>
        ) : hasSaved ? (
          <button style={styles.smallBtn} onClick={() => { setQuickOpen((o) => !o); setOpen(false); }}>
            {quickOpen ? "Cancel" : "Quick Connect"}
          </button>
        ) : (
          <button style={styles.smallBtn} onClick={() => setOpen((o) => !o)}>
            {open ? "Cancel" : "Connect Broker"}
          </button>
        )}
      </div>

      {quickOpen && (
        <form style={styles.form} onSubmit={quickConnect}>
          <label style={styles.fLabel}>PIN</label>
          <input
            style={styles.input}
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            autoFocus
            required
          />

          {error && <div style={styles.error}>{error}</div>}

          <button style={styles.button} disabled={busy}>{busy ? "Connecting..." : "Connect"}</button>

          <span
            style={styles.link}
            onClick={() => { setQuickOpen(false); setOpen(true); setError(""); }}
          >
            Use a different account / update saved details
          </span>
        </form>
      )}

      {open && (
        <form style={styles.form} onSubmit={connect}>
          <label style={styles.fLabel}>Broker</label>
          <select style={styles.input} value={broker} onChange={(e) => setBroker(e.target.value)}>
            {BROKERS.map((b) => (
              <option key={b.value} value={b.value} disabled={b.disabled}>{b.label}</option>
            ))}
          </select>

          <label style={styles.fLabel}>API Key</label>
          <input style={styles.input} value={form.api_key} onChange={set("api_key")} required />

          <label style={styles.fLabel}>Client Code</label>
          <input style={styles.input} value={form.client_code} onChange={set("client_code")} required />

          <label style={styles.fLabel}>PIN</label>
          <input style={styles.input} type="password" value={form.password_or_pin} onChange={set("password_or_pin")} required />

          <label style={styles.fLabel}>TOTP Secret</label>
          <input style={styles.input} value={form.totp_secret} onChange={set("totp_secret")} required />

          {error && <div style={styles.error}>{error}</div>}

          <button style={styles.button} disabled={busy}>{busy ? "Connecting..." : "Connect"}</button>

          {hasSaved && (
            <span
              style={styles.link}
              onClick={() => { setOpen(false); setQuickOpen(true); setError(""); }}
            >
              Back to Quick Connect
            </span>
          )}
        </form>
      )}
    </div>
  );
}

const styles = {
  wrap: { borderBottom: "1px solid var(--border)", background: "var(--panel)", padding: "8px 16px" },
  row: { display: "flex", alignItems: "center", gap: 8 },
  dot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block" },
  label: { fontSize: 12, color: "var(--text-dim)", flex: 1 },
  smallBtn: { background: "var(--panel-alt)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: 5, padding: "5px 10px", fontSize: 11, cursor: "pointer" },
  smallBtnDanger: { background: "var(--panel-alt)", border: "1px solid var(--danger)", color: "var(--danger)", borderRadius: 5, padding: "5px 10px", fontSize: 11, cursor: "pointer" },
  form: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px", marginTop: 10, maxWidth: 520 },
  fLabel: { fontSize: 10, color: "var(--text-dim)", textTransform: "uppercase", alignSelf: "center" },
  input: { background: "var(--panel-alt)", border: "1px solid var(--border)", borderRadius: 5, color: "var(--text)", padding: "6px 8px", fontSize: 12 },
  button: { gridColumn: "span 2", marginTop: 4, background: "var(--accent)", color: "#04140a", border: "none", borderRadius: 5, padding: "8px 0", fontWeight: 600, cursor: "pointer", fontSize: 12 },
  error: { gridColumn: "span 2", color: "var(--danger)", fontSize: 11 },
  link: { gridColumn: "span 2", color: "var(--text-dim)", fontSize: 11, textDecoration: "underline", cursor: "pointer", textAlign: "center", marginTop: 2 },
};
